import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Users, Mail, Phone, MapPin } from "lucide-react";

export default function Customers() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData);
        toast.success("Kunde aktualisiert");
      } else {
        await api.post('/customers', formData);
        toast.success("Kunde erstellt");
      }

      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData({ name: "", email: "", phone: "", address: "" });
      fetchCustomers();
    } catch (error) {
      if (error.response?.status === 403) {
        // Close dialog immediately so modal is visible
        setDialogOpen(false);
        setEditingCustomer(null);
        setFormData({ name: "", email: "", phone: "", address: "" });
        
        // Show upgrade modal
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Speichern");
      }
    }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm(t('customers.confirmDelete'))) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success(t('customers.title') + " gelöscht");
      fetchCustomers();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('customers.title')}</h1>
          <p className="text-lg text-gray-600">{t('customers.subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" onClick={() => {
              setEditingCustomer(null);
              setFormData({ name: "", email: "", phone: "", address: "" });
            }}>
              <Plus className="h-4 w-4 mr-2" />
              {t('customers.newCustomer')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCustomer ? t('customers.editCustomer') : t('customers.newCustomer')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('customers.name')} *</Label>
                <Input
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Max Mustermann GmbH"
                  required
                />
              </div>
              <div>
                <Label>{t('customers.email')}</Label>
                <Input
                  data-testid="customer-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="kontakt@beispiel.de"
                />
              </div>
              <div>
                <Label>{t('customers.phone')}</Label>
                <Input
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>
              <div>
                <Label>{t('customers.address')}</Label>
                <Textarea
                  data-testid="customer-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Straße, PLZ Stadt"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-customer-button">
                {editingCustomer ? t('timeTracking.update') : t('timeTracking.create')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customers.map((customer) => (
          <Card key={customer.id} className="card-hover" data-testid={`customer-card-${customer.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">{customer.name}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                {customer.email && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-start space-x-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{customer.address}</span>
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <Button
                  data-testid={`edit-customer-${customer.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setEditingCustomer(customer);
                    setFormData({
                      name: customer.name,
                      email: customer.email || "",
                      phone: customer.phone || "",
                      address: customer.address || "",
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button
                  data-testid={`delete-customer-${customer.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => deleteCustomer(customer.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {customers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {t('customers.noCustomers')}
          </CardContent>
        </Card>
      )}
      
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        message={upgradeMessage}
        currentPlan={user?.plan}
      />
    </div>
  );
}